import { Siren, MapPin, Clock, CreditCard, Truck } from 'lucide-react';
import DigitalSignatureBlock from '../components/documents/DigitalSignatureBlock';

interface Props {
  data: {
    documentSubtype: 'BOOKING_RECEIPT' | 'TRIP_INVOICE' | 'TRANSPORT_CERTIFICATE' | 'ECC_EVENT_CERTIFICATE' | 'ER_HANDOVER' | 'PARAMEDIC_NOTE' | 'CANCELLATION_RECEIPT';
    bookingId: string;
    bookingDate: string;
    eccEventId?: string;
    vehicleId?: string;
    vehicleType?: string;
    driverId?: string;
    driverName?: string;
    paramedicId?: string;
    paramedicName?: string;
    providerName: string;
    providerNhcId?: string;
    pickupLocation: string;
    dropLocation?: string;
    pickupTime?: string;
    arrivalTime?: string;
    dropTime?: string;
    tripDistance?: string;
    baseFare?: number;
    perKmFare?: number;
    surcharge?: number;
    totalFare?: number;
    amountPaid?: number;
    paymentMode?: string;
    paymentStatus?: string;
    receivingHospital?: string;
    receivingHospitalId?: string;
    handoverId?: string;
    patientCondition?: string;
    vitals?: string;
    treatmentProvided?: string;
    cancellationReason?: string;
    notes?: string[];
    signatories: { name: string; role: string; nhcId?: string; licenseNo?: string; institution?: string }[];
  };
}

function fmt(n: number) { return `Rs. ${n.toLocaleString('en-IN', { minimumFractionDigits: 2 })}`; }

const SUBTYPE_LABELS: Record<string, string> = {
  BOOKING_RECEIPT: 'Ambulance Booking Receipt',
  TRIP_INVOICE: 'Ambulance Trip Invoice',
  TRANSPORT_CERTIFICATE: 'Ambulance Transport Certificate',
  ECC_EVENT_CERTIFICATE: 'Emergency Event Certificate',
  ER_HANDOVER: 'ER Handover Document',
  PARAMEDIC_NOTE: 'Paramedic Care Note',
  CANCELLATION_RECEIPT: 'Ambulance Cancellation Receipt',
};

export default function AmbulanceInvoiceTemplate({ data }: Props) {
  return (
    <div className="space-y-2.5">
      <div className="flex items-center gap-1.5 mb-1">
        <Siren className="w-3.5 h-3.5 text-gray-700" />
        <span className="text-[9px] font-bold uppercase tracking-wider text-gray-700">
          {SUBTYPE_LABELS[data.documentSubtype] || 'Ambulance Document'}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-x-4 gap-y-1 bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <div><span className="text-gray-500">Booking ID:</span> <span className="font-mono text-gray-800">{data.bookingId}</span></div>
        <div><span className="text-gray-500">Date:</span> <span className="text-gray-800">{data.bookingDate}</span></div>
        {data.eccEventId && <div><span className="text-gray-500">Emergency Event:</span> <span className="font-mono text-gray-800">{data.eccEventId}</span></div>}
        <div><span className="text-gray-500">Provider:</span> <span className="text-gray-800">{data.providerName}</span></div>
        {data.vehicleId && <div><span className="text-gray-500">Vehicle:</span> <span className="text-gray-800">{data.vehicleId} {data.vehicleType && `(${data.vehicleType})`}</span></div>}
        {data.driverName && <div><span className="text-gray-500">Driver:</span> <span className="text-gray-800">{data.driverName}</span></div>}
        {data.paramedicName && <div><span className="text-gray-500">Paramedic:</span> <span className="text-gray-800">{data.paramedicName}</span></div>}
      </div>

      {/* Route */}
      <div className="bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
        <p className="text-[8px] font-bold uppercase text-gray-500 mb-1 flex items-center gap-1"><MapPin className="w-2.5 h-2.5" /> Route</p>
        <div className="space-y-1">
          <div><span className="text-gray-500">Pickup:</span> <span className="text-gray-800">{data.pickupLocation}</span></div>
          {data.dropLocation && <div><span className="text-gray-500">Drop:</span> <span className="text-gray-800">{data.dropLocation}</span></div>}
          {data.tripDistance && <div><span className="text-gray-500">Distance:</span> <span className="text-gray-800">{data.tripDistance}</span></div>}
        </div>
        {(data.pickupTime || data.arrivalTime || data.dropTime) && (
          <div className="flex gap-3 mt-1 pt-1 border-t border-gray-200">
            {data.pickupTime && <div><span className="text-gray-500">Dispatched:</span> <span className="text-gray-800">{data.pickupTime}</span></div>}
            {data.arrivalTime && <div><span className="text-gray-500">Arrived:</span> <span className="text-gray-800">{data.arrivalTime}</span></div>}
            {data.dropTime && <div><span className="text-gray-500">Delivered:</span> <span className="text-gray-800">{data.dropTime}</span></div>}
          </div>
        )}
      </div>

      {data.receivingHospital && (
        <div className="bg-gray-50 px-3 py-1.5 border border-gray-200 rounded text-[9px]">
          <span className="text-gray-500">Receiving Hospital:</span> <span className="text-gray-800 font-medium">{data.receivingHospital}</span>
          {data.handoverId && <span className="text-gray-500 ml-2">Handover: <span className="font-mono">{data.handoverId}</span></span>}
        </div>
      )}

      {(data.patientCondition || data.vitals || data.treatmentProvided) && (
        <div className="bg-gray-50 px-3 py-2 border border-gray-200 rounded text-[9px]">
          <p className="text-[8px] font-bold uppercase text-gray-500 mb-1">Clinical Notes</p>
          {data.patientCondition && <div className="mb-0.5"><span className="text-gray-500">Condition:</span> <span className="text-gray-800">{data.patientCondition}</span></div>}
          {data.vitals && <div className="mb-0.5"><span className="text-gray-500">Vitals:</span> <span className="text-gray-800">{data.vitals}</span></div>}
          {data.treatmentProvided && <div><span className="text-gray-500">Treatment:</span> <span className="text-gray-800">{data.treatmentProvided}</span></div>}
        </div>
      )}

      {/* Fare */}
      {data.totalFare !== undefined && (
        <div className="flex justify-end">
          <div className="w-52 bg-gray-50 border border-gray-200 rounded text-[9px]">
            {data.baseFare !== undefined && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Base Fare</span><span className="font-mono text-gray-800">{fmt(data.baseFare)}</span></div>}
            {data.perKmFare !== undefined && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Distance Charge</span><span className="font-mono text-gray-800">{fmt(data.perKmFare)}</span></div>}
            {(data.surcharge ?? 0) > 0 && <div className="flex justify-between py-0.5 px-2.5 border-b border-gray-200"><span className="text-gray-500">Surcharge</span><span className="font-mono text-gray-800">{fmt(data.surcharge!)}</span></div>}
            <div className="flex justify-between py-1 px-2.5 bg-gray-100 font-bold border-b border-gray-300"><span className="text-gray-800">Total</span><span className="font-mono text-gray-900">{fmt(data.totalFare)}</span></div>
            {data.amountPaid !== undefined && <div className="flex justify-between py-0.5 px-2.5"><span className="text-gray-500">Paid</span><span className="font-mono text-green-700">{fmt(data.amountPaid)}</span></div>}
          </div>
        </div>
      )}

      {data.cancellationReason && (
        <div className="bg-red-50 border border-red-200 rounded px-3 py-1.5 text-[9px] text-red-800">
          <span className="font-bold">Cancellation Reason:</span> {data.cancellationReason}
        </div>
      )}

      {data.documentSubtype === 'PARAMEDIC_NOTE' && (
        <div className="bg-amber-50 border border-amber-200 rounded px-3 py-1.5 text-[8px] text-amber-800">
          Placeholder — Detailed paramedic care notes require clinical documentation system integration.
        </div>
      )}

      {data.notes && data.notes.length > 0 && (
        <ul className="list-disc list-inside space-y-0.5 text-[9px] text-gray-700 bg-gray-50 px-3 py-1.5 border border-gray-200 rounded">
          {data.notes.map((n, i) => <li key={i}>{n}</li>)}
        </ul>
      )}

      <DigitalSignatureBlock signatories={data.signatories} />
    </div>
  );
}
